import {useEffect, useState} from 'react'
import {BillingDetailHeader} from './BillingDetailHeader'
import {
  Billing,
  creditCardPaymentResponse,
  Installments,
  InstallmentsRecurrence,
  TBillingInstallment,
} from '../../core/_models'

import {PaymentRecurrentModal} from '../modalRecurrentPayment/paymentModal'
import {ChangeRecurrentModal} from '../modalRecurrentPayment/changeModal'
import {sendLinkChangeCartReq} from '../modalRecurrentPayment/core/_request'
import Swal from 'sweetalert2'
import {PageOverlayer} from '../../../../components/PageOverlayer'
import BillingDetailChangeCard from './BillingDetailChangeCard'
import BillingDetailContent from './BillingDetailContent'
import {getCreditCardPaymentByID} from '../../core/_request'
import {
  changeInstallmentPaymentMethod,
  generateBillingPaymentLink,
  updatePaymentInstallmentStatus,
} from '../core/_requests'
import {copyToPayment} from '../../../../utils/copyToPayment'

interface IBillingProps {
  billing: Billing | null
}

const BillingDetail = ({billing}: IBillingProps) => {
  const options = [
    {
      value: 'change',
      label: 'Alterar forma de pagamento',
      status: [1, 4],
    },
    {
      value: 'payment',
      label: 'Cobrar parcela',
      status: [4],
    },
    {
      value: 'paymentLink',
      label: 'Copiar link de pagamento',
      status: [5],
      // Somente cobrança avulsa (settled billing) pendente no cartão de crédito
      visible: (installment: InstallmentsRecurrence) =>
        installment.settledBilling?.status === 'Pendente' &&
        installment.settledBilling?.paymentOption?.id === 2,
    },
    {
      value: 'viewAcordion',
      label: 'Visualizar',
      status: [1, 2, 3, 4, 5],
    },
    {
      value: 'viewCielo',
      label: 'Visualizar',
      status: [99],
    },
  ]

  const [dataPaymentRecurrent, setDataPaymentRecurrent] = useState<TBillingInstallment>()
  const [dataChangeRecurrent, setDataChangeRecurrent] = useState<TBillingInstallment>()
  // Troca da forma de pagamento de parcela de boleto (billing_installments)
  const [isInstallmentChange, setIsInstallmentChange] = useState(false)
  const [openPaymentRecurrentModal, setOpenPaymentRecurrentModal] = useState(false)
  const [openChangeRecurrentModal, setOpenChangeRecurrentModal] = useState(false)
  const [openDetailModal, setOpenDetailModal] = useState(true)
  const [openViewCollapse, setOpenViewCollapse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dataViewCielo, setDataViewCielo] = useState<creditCardPaymentResponse>()

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  const verifyAction = async (action, installment, billing: Billing | null) => {
    if (action === 'payment') {
      setDataPaymentRecurrent(installment)
      setOpenDetailModal(false)
      setOpenPaymentRecurrentModal(true)
      setOpenChangeRecurrentModal(false)
      setOpenViewCollapse(null)
      return
    }

    if (action === 'paymentLink') {
      setLoading(true)
      await copyToPayment(() =>
        generateBillingPaymentLink(installment.settledBilling.id).finally(() => setLoading(false))
      )
      return
    }

    if (action == 'viewAcordion') {
      setOpenViewCollapse((prevState) => {
        return prevState == installment.id ? null : installment.id
      })
      return
    }

    if (action == 'viewCielo') {
      setLoading(true)
      setOpenViewCollapse((prevState) => {
        return prevState == installment.id ? null : installment.id
      })
      try {
        const data = await getCreditCardPaymentByID(installment.paymentId)
        setDataViewCielo(data)
      } catch (error: any) {
        if (error.response.status === 422) {
          let errorText = ''
          for (let key in error.response.data.errors) {
            errorText += '<li>' + error.response.data.errors[key][0] + '</li>'
          }
          await Swal.fire({
            icon: 'error',
            title: 'Ops! Não foi possível carregar a solcitação!',
            html: errorText,
            customClass: {
              confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
              cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
            },
          })
        } else {
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response.data.message,
            timer: 2000,
            customClass: {
              confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
              cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
            },
          })
        }
        setOpenViewCollapse(null)
      } finally {
        setLoading(false)
      }

      return
    }

    if (action == 'paymentInstallmentStatus') {
      const billingId = Number(billing?.id)
      const installmentId = Number(installment?.id)

      if (!billingId || !installmentId) {
        await Swal.fire({
          icon: 'warning',
          title: 'Dados incompletos',
          text: 'Não foi possível atualizar o status do pagamento.',
          timer: 2000,
        })
        return
      }

      Swal.fire({
        title: 'Deseja atualizar o status do pagamento?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
          cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoading(true)
          try {
            const status = {
              status: 2,
            }
            await updatePaymentInstallmentStatus(billingId, installmentId, status)
            await Swal.fire({
              icon: 'success',
              title: 'Sucesso!!!',
              text: 'Status pagamento atualizado com sucesso!',
              timer: 3000,
            })
            window.location.reload()
          } catch (error: any) {
            if (error.response.status === 422) {
              let errorText = ''
              for (let key in error.response.data.errors) {
                errorText += '<li>' + error.response.data.errors[key][0] + '</li>'
              }
              await Swal.fire({
                icon: 'error',
                title: 'Ops! Não foi possível carregar a solcitação!',
                html: errorText,
                customClass: {
                  confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
                  cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
                },
              })
            } else {
              await Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: error.response.data.message,
                timer: 2000,
                customClass: {
                  confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
                  cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
                },
              })
            }
            setOpenViewCollapse(null)
          } finally {
            setLoading(false)
          }
        }
      })
      return
    }

    setIsInstallmentChange(action === 'changeInstallmentPayment')
    setDataChangeRecurrent(installment)
    setOpenDetailModal(false)
    setOpenPaymentRecurrentModal(false)
    setOpenViewCollapse(null)
    setOpenChangeRecurrentModal(true)
  }

  const closeModals = () => {
    setOpenPaymentRecurrentModal(false)
    setOpenChangeRecurrentModal(false)
    setOpenViewCollapse(null)
    setOpenDetailModal(true)
  }

  const sendLinkChangeCart = async () => {
    Swal.fire({
      title: 'Deseja enviar um link para realizar a troca de cartão ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true)
          const url = await sendLinkChangeCartReq(Number(billing?.recurrencePayment?.id))
          setLoading(false)

          Swal.fire({
            icon: 'success',
            title: 'Success',
            html: `
              <p>Link enviado com sucesso!</p>
              <input type="text" id="swal-copy-url" readonly value="${url}" style="width: 100%; margin-top: 10px; padding: 5px;" />
              <button id="copy-url-btn" style="margin-top: 10px;">Copiar link</button>
            `,
            showConfirmButton: false,
            timer: 10000,
            didOpen: () => {
              const copyButton = document.getElementById('copy-url-btn')
              const input = document.getElementById('swal-copy-url') as HTMLInputElement

              if (copyButton && input) {
                copyButton.addEventListener('click', () => {
                  input.select()
                  input.setSelectionRange(0, 99999) // para dispositivos móveis
                  navigator.clipboard.writeText(input.value).then(() => {
                    copyButton.textContent = 'Copiado!'
                    setTimeout(() => (copyButton.textContent = 'Copiar link'), 2000)
                  })
                })
              }
            },
          })
        } catch (error: any) {
          setLoading(false)
          Swal.fire({
            icon: 'warning',
            title: 'Oops...',
            text: error.response.data.message,
            timer: 3000,
          })
        }
      }
    })
  }

  return (
    <>
      {loading && <PageOverlayer />}

      <div
        className='modal fade show d-block'
        id='kt_modal_add_user'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-800px'>
          <div className='modal-content'>
            <BillingDetailHeader />
            {openDetailModal && !openChangeRecurrentModal && !openPaymentRecurrentModal && (
              <BillingDetailChangeCard billing={billing} sendLinkChangeCart={sendLinkChangeCart}>
                <div className='table-responsive'>
                  {billing?.recurrencePayment ? (
                    <BillingDetailContent
                      billing={billing}
                      verifyAction={verifyAction}
                      options={options}
                      openViewCollapse={openViewCollapse}
                    />
                  ) : (
                    <BillingDetailContent
                      billing={billing}
                      verifyAction={verifyAction}
                      options={options}
                      openViewCollapse={openViewCollapse}
                      isRecurrencePayment={false}
                      dataViewCielo={dataViewCielo}
                      loading={loading}
                    />
                  )}
                </div>
              </BillingDetailChangeCard>
            )}
            {openPaymentRecurrentModal && !openDetailModal && !openChangeRecurrentModal && (
              <PaymentRecurrentModal
                billingInstallment={dataPaymentRecurrent}
                billing={billing}
                closeModal={() => closeModals()}
              />
            )}

            {openChangeRecurrentModal && !openDetailModal && !openPaymentRecurrentModal && (
              <ChangeRecurrentModal
                billingInstallment={dataChangeRecurrent}
                billing={billing}
                closeModal={() => closeModals()}
                submitPayment={
                  isInstallmentChange
                    ? (data) =>
                        changeInstallmentPaymentMethod(
                          Number(billing?.id),
                          Number(dataChangeRecurrent?.id),
                          data
                        )
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {BillingDetail}
